/*
 * Self-contained controller for the "Customise Your Own YouTube / Instagram
 * Handle" products. Forked from custom-personalization-multi-add.js (cart
 * flow) + custom-personalization-text.js (live preview), specialised for the
 * social-handle case: a Platform choice (YouTube / Instagram), a full-colour
 * platform logo in the preview, and a leading "@" auto-added to Instagram
 * handles.
 *
 * Deliberately does NOT reuse custom-personalization-multi-add.js -- that file
 * is shared with the live text/image products and has no notion of a Platform
 * property. Keeping this standalone means the social products need zero
 * changes to any existing theme file.
 *
 * Cart line-item properties emitted (read by operations-os
 * src/lib/customized-demand.ts): "Custom Text", "Font", "Color", "Platform".
 */
document.querySelectorAll('[data-custom-personalization][data-mode="social"]').forEach(function (root) {
  var textInput = root.querySelector('[data-custom-text-input]');
  var textLabel = root.querySelector('[data-custom-text-label]');
  var platformSelect = root.querySelector('[data-custom-platform-select]');
  var fontSelect = root.querySelector('[data-custom-font-select]');
  var colorSelect = root.querySelector('[data-custom-color-select]');
  var preview = root.querySelector('[data-custom-text-preview]');
  var previewLabel = root.querySelector('[data-custom-text-preview-label]');
  var previewLogo = root.querySelector('[data-custom-social-logo]');

  var submitButton = root.querySelector('[data-custom-personalization-submit]');
  var submitButtonText = root.querySelector('[data-custom-personalization-submit-text]');
  var errorWrapper = root.querySelector('[data-custom-personalization-error]');
  var errorText = root.querySelector('[data-custom-personalization-error-text]');
  var primaryQtyInput = root.querySelector('[data-primary-qty-input]');
  var openMoreSizesButton = root.querySelector('[data-open-more-sizes]');

  if (
    !textInput || !platformSelect || !fontSelect || !colorSelect || !preview || !previewLabel || !previewLogo ||
    !submitButton || !errorWrapper || !errorText || !primaryQtyInput
  ) {
    return;
  }

  var EMPTY_LABEL = 'your handle';

  /* ------------------------------------------------------------------ *
   * Custom size + size gating (ported from custom-personalization-
   * multi-add.js, 2026-10-05 -- see that file's comments for the full
   * rationale on each piece: round-up-to-next-tier pricing, blur-only
   * commit (no pill flicker while typing), no visible pill highlight for
   * a custom-size-driven selection, and locking size until the rest of
   * the customization is filled in). Adapted here for 4 required fields
   * (Platform, handle, Font, Color) instead of 3.
   * ------------------------------------------------------------------ */
  var customSizeInput = root.querySelector('[data-custom-size-input]');
  var customSizeField = root.querySelector('[data-custom-size-field]');
  var customSizeErrorWrapper = root.querySelector('[data-custom-size-error]');
  var customSizeErrorText = root.querySelector('[data-custom-size-error-text]');
  var customSizeConfirm = root.querySelector('[data-custom-size-confirm]');
  var customSizeActiveValue = null;
  var customSizeHasError = false;

  var sizeFieldset = root.querySelector('fieldset[data-size-fieldset]');
  var sizeGateMessage = root.querySelector('[data-size-gate-message]');
  var sizeGateMessageText = root.querySelector('[data-size-gate-message-text]');

  function missingFieldMessage() {
    if (!platformSelect.value) return 'Please choose YouTube or Instagram first.';
    if (!currentHandle()) return 'Please enter your handle first.';
    if (!fontSelect.value) return 'Please choose a font first.';
    if (!colorSelect.value) return 'Please choose a color first.';
    return '';
  }

  function updateSizeGate() {
    if (!sizeGateMessage) return;
    var ready = missingFieldMessage() === '';
    root.querySelectorAll('[data-primary-size-radio][data-size-gated]').forEach(function (radio) {
      radio.disabled = !ready;
    });
    if (customSizeInput && customSizeInput.hasAttribute('data-size-gated')) {
      customSizeInput.readOnly = !ready;
    }
    if (ready) sizeGateMessage.hidden = true;
  }

  function showSizeGateMessage() {
    var msg = missingFieldMessage();
    if (!msg || !sizeGateMessage) return;
    if (sizeGateMessageText) sizeGateMessageText.textContent = msg;
    sizeGateMessage.hidden = false;
  }

  if (sizeFieldset && sizeGateMessage) {
    sizeFieldset.addEventListener('click', function () {
      if (missingFieldMessage()) showSizeGateMessage();
    });
  }

  if (customSizeField && sizeGateMessage) {
    customSizeField.addEventListener('click', function () {
      if (missingFieldMessage()) {
        showSizeGateMessage();
        if (customSizeInput) customSizeInput.blur();
      }
    });
  }

  function sizeFromRadio(radio) {
    var label = root.querySelector('label[for="' + radio.id + '"]');
    var text = label ? label.textContent : '';
    var match = text.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  }

  function clearCustomSizeError() {
    customSizeHasError = false;
    if (!customSizeErrorWrapper) return;
    customSizeErrorWrapper.hidden = true;
    if (customSizeErrorText) customSizeErrorText.textContent = '';
  }

  function showCustomSizeError(message) {
    customSizeHasError = true;
    if (!customSizeErrorWrapper || !customSizeErrorText) return;
    customSizeErrorText.textContent = message;
    customSizeErrorWrapper.hidden = false;
  }

  function clearCustomSizeConfirm() {
    if (!customSizeConfirm) return;
    customSizeConfirm.hidden = true;
    customSizeConfirm.textContent = '';
  }

  function clearCustomSizeDrivenAttr() {
    var marked = root.querySelector('[data-primary-size-radio][data-custom-size-driven]');
    if (marked) marked.removeAttribute('data-custom-size-driven');
  }

  function validateCustomSize(raw) {
    clearCustomSizeError();
    if (!/^\d+(\.\d)?$/.test(raw)) {
      showCustomSizeError('Enter a size in inches, up to 1 decimal place (e.g. 5.5).');
      return null;
    }
    var value = Math.round(parseFloat(raw) * 10) / 10;
    var min = parseFloat(customSizeInput.dataset.minSize);
    var max = parseFloat(customSizeInput.dataset.maxSize);
    if (value < min || value > max) {
      showCustomSizeError('Size must be between ' + min + '" and ' + max + '".');
      return null;
    }
    return value;
  }

  function onCustomSizeInput() {
    var raw = customSizeInput.value.trim();
    if (!raw) {
      clearCustomSizeError();
    } else {
      validateCustomSize(raw);
    }
    refreshSubmitGate();
  }

  function onCustomSizeBlur() {
    var raw = customSizeInput.value.trim();
    clearCustomSizeConfirm();
    customSizeActiveValue = null;

    if (!raw) {
      clearCustomSizeError();
      refreshSubmitGate();
      return;
    }

    var value = validateCustomSize(raw);
    if (value === null) {
      refreshSubmitGate();
      return;
    }

    var best = null;
    var bestSize = null;
    root.querySelectorAll('[data-primary-size-radio]').forEach(function (radio) {
      var size = sizeFromRadio(radio);
      if (size === null || size + 0.001 < value) return;
      if (bestSize === null || size < bestSize) {
        bestSize = size;
        best = radio;
      }
    });

    if (!best) {
      showCustomSizeError(
        'Size must be between ' + customSizeInput.dataset.minSize + '" and ' + customSizeInput.dataset.maxSize + '".'
      );
      refreshSubmitGate();
      return;
    }

    customSizeActiveValue = String(value);
    clearCustomSizeDrivenAttr();
    root.dataset.syncingCustomSize = 'true';
    best.checked = true;
    best.setAttribute('data-custom-size-driven', '');
    best.dispatchEvent(new Event('change', { bubbles: true }));
    delete root.dataset.syncingCustomSize;

    if (customSizeConfirm) {
      customSizeConfirm.hidden = false;
      customSizeConfirm.textContent = "We'll size your design to exactly " + value + '".';
    }
    refreshSubmitGate();
  }

  if (customSizeInput) {
    customSizeInput.addEventListener('input', onCustomSizeInput);
    customSizeInput.addEventListener('blur', onCustomSizeBlur);
    customSizeInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') customSizeInput.blur();
    });
  }

  // Instagram values carry a leading "@"; YouTube channel names are plain.
  // A customer typing their own "@" is de-duped, not doubled.
  function normalizeHandle(raw, platform) {
    var value = (raw || '').trim();
    if (platform === 'Instagram') {
      value = value.replace(/^@+/, '');
      if (value) value = '@' + value;
    }
    return value;
  }

  function currentHandle() {
    return normalizeHandle(textInput.value, platformSelect.value);
  }

  /* ------------------------------------------------------------------ *
   * Live preview
   * ------------------------------------------------------------------ */
  function updatePreview() {
    var platform = platformSelect.value;
    var handle = currentHandle();
    var fontOption = fontSelect.options[fontSelect.selectedIndex];
    var fontFamily = fontOption ? fontOption.getAttribute('data-font-family') : null;
    var color = colorSelect.value;

    previewLabel.textContent = handle || EMPTY_LABEL;
    preview.toggleAttribute('data-empty', !handle);
    previewLabel.style.fontFamily = fontFamily ? "'" + fontFamily + "'" : '';
    previewLabel.style.color = color === 'Black' ? '#111111' : color === 'White' ? '#ffffff' : '';
    preview.style.background = color === 'White' ? '#2b2b2b' : color === 'Black' ? '#f5f5f5' : '';

    if (platform === 'Instagram') {
      previewLogo.src = previewLogo.dataset.srcInstagram;
      previewLogo.hidden = false;
    } else if (platform === 'YouTube') {
      previewLogo.src = previewLogo.dataset.srcYoutube;
      previewLogo.hidden = false;
    } else {
      previewLogo.src = previewLogo.dataset.srcYoutube;
      previewLogo.hidden = false;
    }

    var fieldText =
      platform === 'Instagram' ? 'Your Instagram handle' :
      platform === 'YouTube' ? 'Your YouTube channel name' :
      'Your handle / channel name';
    if (textLabel) textLabel.textContent = fieldText;
    textInput.placeholder = platform === 'Instagram' ? 'yourhandle' : fieldText;
  }

  textInput.addEventListener('input', updatePreview);
  platformSelect.addEventListener('change', updatePreview);
  fontSelect.addEventListener('change', updatePreview);
  colorSelect.addEventListener('change', updatePreview);
  updatePreview();

  function resetPersonalization() {
    textInput.value = '';
    platformSelect.selectedIndex = 0;
    fontSelect.selectedIndex = 0;
    colorSelect.selectedIndex = 0;
    updatePreview();
  }

  function prefillPersonalization(data) {
    if (data.platform) platformSelect.value = data.platform;
    if (data.text) {
      // Stored value may already carry the "@" -- show it without the prefix
      // in the input so re-submitting doesn't stack a second one.
      textInput.value = data.platform === 'Instagram' ? String(data.text).replace(/^@+/, '') : data.text;
    }
    if (data.font) fontSelect.value = data.font;
    if (data.color) colorSelect.value = data.color;
    updatePreview();
  }

  /* ------------------------------------------------------------------ *
   * Size list + cart flow (forked from custom-personalization-multi-add.js)
   * ------------------------------------------------------------------ */
  var promptText = 'Add your handle';

  function syncRowEmphasis(input) {
    var row = input.closest('.custom-personalization__size-row');
    if (!row) return;
    var quantity = parseInt(input.value, 10) || 0;
    row.toggleAttribute('data-has-qty', quantity > 0);
  }

  function qtyStepperButtons() {
    document.querySelectorAll('[data-qty-decrease]').forEach(function (btn) {
      if (btn.dataset.boundToPersonalization) return;
      btn.dataset.boundToPersonalization = 'true';
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.custom-personalization__qty');
        var input = wrapper.querySelector('input');
        var min = parseInt(input.min, 10) || 0;
        input.value = Math.max(min, (parseInt(input.value, 10) || 0) - 1);
        syncRowEmphasis(input);
      });
    });
    document.querySelectorAll('[data-qty-increase]').forEach(function (btn) {
      if (btn.dataset.boundToPersonalization) return;
      btn.dataset.boundToPersonalization = 'true';
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.custom-personalization__qty');
        var input = wrapper.querySelector('input');
        input.value = (parseInt(input.value, 10) || 0) + 1;
        syncRowEmphasis(input);
      });
    });
    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      if (input.dataset.emphasisBound) return;
      input.dataset.emphasisBound = 'true';
      input.addEventListener('input', function () {
        syncRowEmphasis(input);
      });
    });
  }
  qtyStepperButtons();

  var modalSubmitButton = document.querySelector('[data-modal-submit]');
  if (modalSubmitButton && !modalSubmitButton.dataset.boundToPersonalization) {
    modalSubmitButton.dataset.boundToPersonalization = 'true';
    modalSubmitButton.addEventListener('click', function () {
      submitButton.click();
    });
  }

  function showError(message) {
    errorText.textContent = message;
    errorWrapper.hidden = false;
  }

  function clearError() {
    errorWrapper.hidden = true;
    errorText.textContent = '';
  }

  function setLoading(loading) {
    submitButton.toggleAttribute('aria-disabled', loading);
    submitButton.classList.toggle('loading', loading);
    var spinner = submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);

    if (modalSubmitButton) {
      modalSubmitButton.disabled = loading;
      modalSubmitButton.classList.toggle('loading', loading);
      var modalSpinner = modalSubmitButton.querySelector('.loading__spinner');
      if (modalSpinner) modalSpinner.classList.toggle('hidden', !loading);
    }
  }

  function isCustomizationComplete() {
    return !!(currentHandle() && platformSelect.value && fontSelect.value && colorSelect.value);
  }

  function syncNativePrice() {
    var nativePriceWrapper = document.querySelector('[id^="price-"]');
    if (nativePriceWrapper) nativePriceWrapper.style.display = 'none';

    var ownPrice = root.querySelector('[data-custom-personalization-price]');
    if (!ownPrice) return;

    var primaryRadio = root.querySelector('[data-primary-size-radio]:checked');
    if (!primaryRadio) {
      ownPrice.hidden = true;
      return;
    }

    var row = document.querySelector('[data-qty-input][data-variant-id="' + primaryRadio.value + '"]');
    var priceRow = row ? row.closest('.custom-personalization__size-row') : null;
    var priceText = priceRow ? priceRow.querySelector('.custom-personalization__size-price').textContent : '';

    ownPrice.textContent = priceText;
    ownPrice.hidden = !priceText;
  }

  function refreshSubmitGate() {
    var complete = isCustomizationComplete();
    var sizeSelected = !!root.querySelector('[data-primary-size-radio]:checked');

    if (customSizeHasError) {
      submitButton.disabled = true;
      submitButtonText.textContent = 'Fix custom size';
    } else if (!complete) {
      submitButton.disabled = true;
      submitButtonText.textContent = promptText;
    } else if (!sizeSelected) {
      submitButton.disabled = true;
      submitButtonText.textContent = 'Select size';
    } else {
      submitButton.disabled = false;
      submitButtonText.textContent = 'Add to cart';
    }

    if (openMoreSizesButton) openMoreSizesButton.disabled = !(complete && sizeSelected && !customSizeHasError);
    syncNativePrice();
  }

  refreshSubmitGate();
  updateSizeGate();

  root.querySelectorAll('[data-primary-size-radio]').forEach(function (radio) {
    radio.addEventListener('change', refreshSubmitGate);
  });

  // Real (not synthetic) click on a standard pill after a custom size was
  // active means the customer changed their mind.
  root.querySelectorAll('[data-primary-size-radio]').forEach(function (radio) {
    radio.addEventListener('change', function () {
      if (root.dataset.syncingCustomSize === 'true') return;
      if (customSizeInput) customSizeInput.value = '';
      customSizeActiveValue = null;
      clearCustomSizeError();
      clearCustomSizeConfirm();
      clearCustomSizeDrivenAttr();
    });
  });

  [textInput, platformSelect, fontSelect, colorSelect].forEach(function (el) {
    el.addEventListener('input', refreshSubmitGate);
    el.addEventListener('change', refreshSubmitGate);
    el.addEventListener('input', updateSizeGate);
    el.addEventListener('change', updateSizeGate);
  });

  function syncModalWithPrimary() {
    var primaryRadio = root.querySelector('[data-primary-size-radio]:checked');
    var primaryVariantId = primaryRadio ? primaryRadio.value : null;

    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      var row = input.closest('.custom-personalization__size-row');
      var isSoldOut = input.hasAttribute('data-sold-out');
      var isPrimary = primaryVariantId && input.dataset.variantId === primaryVariantId;
      var wasPrimary = row && row.hasAttribute('data-linked-to-primary');

      if (isPrimary) {
        input.value = primaryQtyInput.value;
        input.disabled = true;
        if (row) row.setAttribute('data-linked-to-primary', '');
      } else {
        if (wasPrimary) {
          input.value = 0;
          if (row) row.removeAttribute('data-has-qty');
        }
        if (!isSoldOut) input.disabled = false;
        if (row) row.removeAttribute('data-linked-to-primary');
      }
    });
  }

  if (openMoreSizesButton) {
    openMoreSizesButton.addEventListener('click', syncModalWithPrimary);
  }
  primaryQtyInput.addEventListener('input', syncModalWithPrimary);
  root.querySelectorAll('[data-primary-size-radio]').forEach(function (radio) {
    radio.addEventListener('change', syncModalWithPrimary);
  });

  function collectSelections() {
    var byVariant = {};

    var primaryRadio = root.querySelector('[data-primary-size-radio]:checked');
    var primaryVariantId = primaryRadio ? primaryRadio.value : null;

    if (primaryRadio) {
      var primaryQty = parseInt(primaryQtyInput.value, 10) || 0;
      if (primaryQty > 0) byVariant[primaryVariantId] = primaryQty;
    }

    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      if (input.dataset.variantId === primaryVariantId) return;
      var quantity = parseInt(input.value, 10) || 0;
      if (quantity > 0) {
        var variantId = input.dataset.variantId;
        byVariant[variantId] = (byVariant[variantId] || 0) + quantity;
      }
    });

    return Object.keys(byVariant).map(function (variantId) {
      return { variantId: variantId, quantity: byVariant[variantId] };
    });
  }

  function collectProperties() {
    var handle = currentHandle();
    if (!handle) return { error: 'Please enter your handle.' };
    if (!platformSelect.value) return { error: 'Please choose YouTube or Instagram.' };
    if (!fontSelect.value) return { error: 'Please choose a font.' };
    if (!colorSelect.value) return { error: 'Please choose a color.' };
    if (customSizeHasError) return { error: 'Please fix the custom size, or clear it to use a standard size.' };

    var properties = {
      'Custom Text': handle,
      Font: fontSelect.value,
      Color: colorSelect.value,
      Platform: platformSelect.value,
    };
    if (customSizeActiveValue) properties['Custom Size (inches)'] = customSizeActiveValue;

    return {
      properties: properties,
    };
  }

  function addItemJson(variantId, quantity, properties, sectionsParam) {
    var body = { id: variantId, quantity: quantity, properties: properties };
    if (sectionsParam) {
      body.sections = sectionsParam;
      body.sections_url = window.location.pathname;
    }
    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json();
    });
  }

  function addItemsBulk(items, sectionsParam) {
    var body = { items: items };
    if (sectionsParam) {
      body.sections = sectionsParam;
      body.sections_url = window.location.pathname;
    }
    return fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json();
    });
  }

  function renderCartSections(response) {
    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (cart && typeof cart.renderContents === 'function' && response.sections) {
      cart.renderContents(response);
    }
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) cartDrawerEl.classList.remove('is-empty');
    var cartDrawerItemsEl = document.querySelector('cart-drawer-items');
    if (cartDrawerItemsEl) cartDrawerItemsEl.classList.remove('is-empty');
  }

  function resetForm() {
    resetPersonalization();

    root.querySelectorAll('[data-primary-size-radio]').forEach(function (r) {
      r.checked = false;
    });
    primaryQtyInput.value = 1;

    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      input.value = 0;
      if (!input.hasAttribute('data-sold-out')) input.disabled = false;
      var row = input.closest('.custom-personalization__size-row');
      if (row) {
        row.removeAttribute('data-linked-to-primary');
        row.removeAttribute('data-has-qty');
      }
    });

    var modalOpener = root.querySelector('modal-opener[data-modal]');
    var modal = modalOpener ? document.querySelector(modalOpener.getAttribute('data-modal')) : null;
    if (modal && typeof modal.hide === 'function') modal.hide();

    if (customSizeInput) customSizeInput.value = '';
    customSizeActiveValue = null;
    clearCustomSizeError();
    clearCustomSizeConfirm();
    clearCustomSizeDrivenAttr();
    if (sizeGateMessage) sizeGateMessage.hidden = true;

    clearError();
    refreshSubmitGate();
    updateSizeGate();
  }

  submitButton.addEventListener('click', function () {
    clearError();

    var selections = collectSelections();
    if (selections.length === 0) {
      showError('Please choose a quantity for at least one size.');
      return;
    }

    var custom = collectProperties();
    if (custom.error) {
      showError(custom.error);
      return;
    }

    setLoading(true);

    var sectionsParam = 'cart-drawer,cart-icon-bubble';
    var first = selections[0];
    var rest = selections.slice(1);
    var isLastCallForFirst = rest.length === 0;

    addItemJson(first.variantId, first.quantity, custom.properties, isLastCallForFirst ? sectionsParam : null)
      .then(function (firstResponse) {
        if (firstResponse.status) {
          throw new Error(firstResponse.description || firstResponse.message || 'Could not add to cart.');
        }
        if (rest.length === 0) return firstResponse;

        var bulkItems = rest.map(function (selection) {
          return { id: selection.variantId, quantity: selection.quantity, properties: custom.properties };
        });

        return addItemsBulk(bulkItems, sectionsParam).then(function (response) {
          if (response.status || response.description) {
            throw new Error(response.description || response.message || 'Could not add all sizes to cart.');
          }
          return response;
        });
      })
      .then(function (finalResponse) {
        renderCartSections(finalResponse);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-personalization',
            productVariantId: first.variantId,
            cartData: finalResponse,
          });
        }
        resetForm();
      })
      .catch(function (err) {
        showError(err.message || 'Something went wrong adding these to your cart. Please try again.');
      })
      .finally(function () {
        setLoading(false);
      });
  });

  // Edit-from-cart: the cart "Edit" link (custom-personalization-edit-link.liquid)
  // sends size/text/font/color back as query params. Platform is not carried by
  // that shared snippet yet, so it falls back to a fresh pick -- harmless.
  var params = new URLSearchParams(window.location.search);
  if (params.get('edit_variant')) {
    var editVariantId = params.get('edit_variant');
    var radio = root.querySelector('[data-primary-size-radio][value="' + editVariantId + '"]');
    if (radio) {
      radio.checked = true;
      refreshSubmitGate();
    }
    if (params.get('edit_qty')) primaryQtyInput.value = params.get('edit_qty');

    prefillPersonalization({
      platform: params.get('edit_platform') || '',
      text: params.get('edit_text') || '',
      font: params.get('edit_font') || '',
      color: params.get('edit_color') || '',
    });
    refreshSubmitGate();
  }
});
